/**
 * Implantes que el profesional ya colocó antes de usar Donna («historial anterior»): fecha de la cirugía, implantes
 * (marca, línea, medida), rehabilitación si ya se hizo y el cálculo de la osteointegración desde la fecha de la cirugía.
 * No es una cirugía firmada en Donna: solo se registra lo que el profesional declara.
 */
import { sumarDias } from "./agenda.ts";
import { fechasImplante, sistemaImplante, type PlanImplante } from "./implantes.ts";

export interface RehabilitacionHistorial { hecha: boolean; fecha: string | null; tipo: string; nota: string; color?: string }
export interface HistorialImplante { rehabilitacion: RehabilitacionHistorial }

export const historialVacio = (): HistorialImplante => ({ rehabilitacion: { hecha: false, fecha: null, tipo: "", nota: "", color: "" } });

/** La cirugía no puede ser futura ni anterior a 1990; la rehabilitación, después de la cirugía y no futura. */
export function problemasHistorialImplante(fecha: string | null, h: HistorialImplante, hoy: string): string[] {
  const e: string[] = [];
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (!fecha || !iso.test(fecha)) e.push("Indica la fecha de la cirugía.");
  else if (fecha > hoy) e.push("La fecha de la cirugía no puede ser futura.");
  else if (fecha < "1990-01-01") e.push("La fecha de la cirugía no es válida.");
  const r = h.rehabilitacion;
  if (r.hecha) {
    if (!r.fecha || !iso.test(r.fecha)) e.push("Indica la fecha de la rehabilitación o desmarca «ya tiene la prótesis».");
    else if (r.fecha > hoy) e.push("La fecha de la rehabilitación no puede ser futura.");
    else if (fecha && iso.test(fecha) && r.fecha < fecha) e.push("La rehabilitación no puede ser anterior a la cirugía.");
  }
  return e;
}

export interface EstadoOsteointegracion { meses: number; fin: string; cumplida: boolean; dias: number }

/** Desde la fecha de la cirugía: cuándo termina la osteointegración y si ya se cumplió (`dias` = días que faltan o que pasaron desde entonces). */
export function estadoOsteointegracion(fechaCirugia: string, meses: number, hoy: string): EstadoOsteointegracion {
  const fin = sumarDias(fechaCirugia, meses * 30);
  const dias = Math.round((Date.parse(fin + "T12:00:00Z") - Date.parse(hoy + "T12:00:00Z")) / 86400000);
  return { meses, fin, cumplida: dias <= 0, dias: Math.abs(dias) };
}

const unidad = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;
const duracion = (dias: number) => (dias >= 60 ? unidad(Math.round(dias / 30), "mes", "meses") : unidad(dias, "día", "días"));

/** Frase para la pantalla: «La osteointegración (3 meses) se cumplió hace 4 meses» o «faltan 3 semanas». */
export function textoOsteointegracion(e: EstadoOsteointegracion): string {
  const base = `La osteointegración (${unidad(e.meses, "mes", "meses")})`;
  if (e.dias === 0) return `${base} se cumple hoy.`;
  return e.cumplida ? `${base} se cumplió hace ${duracion(e.dias)}.` : `${base} se cumple en ${duracion(e.dias)}.`;
}

/** Fechas calculadas desde la cirugía anterior (retiro de sutura, osteointegración, segunda fase y carga). */
export function fechasDeHistorial(plan: PlanImplante, fechaCirugia: string) {
  return fechasImplante(plan, fechaCirugia);
}

const medida = (d: number | null, l: number | null) => [d !== null ? `Ø ${d}` : "", l !== null ? `${l} mm` : ""].filter(Boolean).join(" × ");

/** Texto corto del historial: implantes, fecha y rehabilitación. */
export function resumenHistorialImplante(plan: PlanImplante, fecha: string, h: HistorialImplante): string {
  const lista = plan.implantes.map((i) => `pieza ${i.pieza}: ${[i.marca, i.linea].filter(Boolean).join(" ")} ${medida(i.diametro, i.longitud)}`.trim()).join("; ");
  const r = h.rehabilitacion;
  return `Implantes colocados el ${fecha} (historial anterior): ${lista}.` + (r.hecha ? ` Rehabilitación${r.tipo ? ` (${r.tipo.toLowerCase()})` : ""}${r.color ? `, color ${r.color}` : ""} el ${r.fecha}.` : " Aún sin rehabilitación.");
}

/** Los meses de osteointegración que Donna calcula para el plan (o el que eligió el profesional). */
export function mesesDeHistorial(plan: PlanImplante): number {
  return fechasImplante(plan, "2000-01-01").mesesOsteointegracion;
}

export const marcaConocida = (marca: string, linea: string) => sistemaImplante(marca, linea) !== null;
